import React, { useState } from 'react';
import { Form, Button, ListGroup } from 'react-bootstrap';
import { getFilteredPinsForAction } from '../../utils/devicePins';
import { generateEntityId } from '../../utils/deviceIds';

const DeviceActionPinsPanel = ({ sensors, actionPins, setActionPins, deviceType, board }) => {
  const [currentActionName, setCurrentActionName] = useState('');
  const [currentActionType, setCurrentActionType] = useState('');
  const [currentActionPin, setCurrentActionPin] = useState('');
  const [currentLightType, setCurrentLightType] = useState('');
  const [currentNumLights, setCurrentNumLights] = useState('');

  const addActionPin = () => {
    if (currentActionName && currentActionType && currentActionPin) {
      const newActionPin = {
        actionPinID: generateEntityId(),
        name: currentActionName,
        type: currentActionType,
        pin: currentActionPin,
      };
      if (currentActionType === 'light') {
        newActionPin.lightType = currentLightType;
        if (currentLightType === '3 Lead LED Strip') {
          newActionPin.numLights = currentNumLights;
        }
      }
      setActionPins([...actionPins, newActionPin]);
      setCurrentActionName('');
      setCurrentActionType('');
      setCurrentActionPin('');
      setCurrentLightType('');
      setCurrentNumLights('');
    }
  };

  const removeActionPin = (index) => {
    setActionPins(actionPins.filter((_, i) => i !== index));
  };

  const updateActionPin = (index, field, value) => {
    const updatedActionPins = [...actionPins];
    updatedActionPins[index] = { ...updatedActionPins[index], [field]: value };
    setActionPins(updatedActionPins);
  };

  const pinOptions = (actionType, lightType, excludeIndex = -1) => getFilteredPinsForAction({
    sensors,
    actionPins,
    deviceType,
    board,
    actionType,
    lightType,
    excludeIndex,
  });

  return (
    <div className="setup-panel">
      <h4 className="mb-3">Manage Action Pins</h4>
      <Form.Group className="mb-3">
        <Form.Label>Add Action Pin</Form.Label>
        <div className="d-flex gap-3 mb-2 flex-wrap">
          <Form.Control
            type="text"
            value={currentActionName}
            onChange={(e) => setCurrentActionName(e.target.value)}
            placeholder="Name (e.g., Living Room Light)"
            className="flex-grow-1"
          />
          <Form.Select
            value={currentActionType}
            onChange={(e) => {
              setCurrentActionType(e.target.value);
              if (e.target.value !== 'light') {
                setCurrentLightType('');
                setCurrentNumLights('');
              }
            }}
            className="flex-grow-1"
          >
            <option value="">Select action type</option>
            <option value="light">Light</option>
            <option value="water_pump">Water Pump</option>
          </Form.Select>
          {currentActionType === 'light' && (
            <Form.Select
              value={currentLightType}
              onChange={(e) => {
                setCurrentLightType(e.target.value);
                if (e.target.value !== '3 Lead LED Strip') {
                  setCurrentNumLights('');
                }
              }}
              className="flex-grow-1"
            >
              <option value="">Select light type</option>
              <option value="Simple On/Off">Simple On/Off</option>
              <option value="3 Lead LED Strip">3 Lead LED Strip</option>
            </Form.Select>
          )}
          {currentActionType === 'light' && currentLightType === '3 Lead LED Strip' && (
            <Form.Control
              type="number"
              value={currentNumLights}
              onChange={(e) => setCurrentNumLights(e.target.value)}
              placeholder="Number of Lights"
              className="flex-grow-1"
              min="1"
            />
          )}
          <Form.Select value={currentActionPin} onChange={(e) => setCurrentActionPin(e.target.value)}>
            <option value="">Select pin</option>
            {pinOptions(currentActionType, currentLightType).map((pin) => (
              <option key={pin} value={pin}>{pin}</option>
            ))}
          </Form.Select>
          <Button variant="outline-primary" onClick={addActionPin}>Add Action Pin</Button>
        </div>
      </Form.Group>

      {actionPins.length > 0 && (
        <div className="mb-3">
          <h4 className="mb-3">Current Action Pins:</h4>
          <ListGroup>
            {actionPins.map((actionPin, index) => (
              <ListGroup.Item key={index} className="d-flex gap-3 align-items-center flex-wrap">
                <Form.Control
                  type="text"
                  value={actionPin.name}
                  onChange={(e) => updateActionPin(index, 'name', e.target.value)}
                  placeholder="Name"
                  className="w-auto flex-grow-1"
                />
                <Form.Select
                  value={actionPin.type}
                  onChange={(e) => {
                    updateActionPin(index, 'type', e.target.value);
                    if (e.target.value !== 'light') {
                      updateActionPin(index, 'lightType', '');
                      updateActionPin(index, 'numLights', '');
                    }
                  }}
                  className="w-auto flex-grow-1"
                >
                  <option value="light">Light</option>
                  <option value="water_pump">Water Pump</option>
                </Form.Select>
                {actionPin.type === 'light' && (
                  <Form.Select
                    value={actionPin.lightType || ''}
                    onChange={(e) => {
                      updateActionPin(index, 'lightType', e.target.value);
                      if (e.target.value !== '3 Lead LED Strip') {
                        updateActionPin(index, 'numLights', '');
                      }
                    }}
                    className="w-auto flex-grow-1"
                  >
                    <option value="">Select light type</option>
                    <option value="Simple On/Off">Simple On/Off</option>
                    <option value="3 Lead LED Strip">3 Lead LED Strip</option>
                  </Form.Select>
                )}
                {actionPin.type === 'light' && actionPin.lightType === '3 Lead LED Strip' && (
                  <Form.Control
                    type="number"
                    value={actionPin.numLights || ''}
                    onChange={(e) => updateActionPin(index, 'numLights', e.target.value)}
                    placeholder="Number of Lights"
                    className="w-auto flex-grow-1"
                    min="1"
                  />
                )}
                <Form.Select
                  value={actionPin.pin}
                  onChange={(e) => updateActionPin(index, 'pin', e.target.value)}
                  className="w-auto"
                >
                  <option value="">Select pin</option>
                  {pinOptions(actionPin.type, actionPin.lightType, index).map((pin) => (
                    <option key={pin} value={pin}>{pin}</option>
                  ))}
                </Form.Select>
                <Button variant="outline-danger" onClick={() => removeActionPin(index)}>Remove</Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
};

export default DeviceActionPinsPanel;